import { Component, OnInit, Output, EventEmitter, Input, ViewChild, Renderer2, ElementRef } from '@angular/core';
import { Observable, Subscription, BehaviorSubject } from 'rxjs';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DocumentDetails, DocumentRequest, Document } from 'src/app/model/document';
import { DocumentService } from 'src/app/services/document.service';
import { FileUtil } from 'src/app/shared/file.util';
import { environment } from 'src/environments/environment';
import { IsLoadingService } from 'src/app/services/loading.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-upload',
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.scss']
})
export class UploadComponent implements OnInit {

  @Input() showModal;
  @Input() docType;
  @Input() uploadType;
  @Input() labelHeader;
  @Input() documentRequest: DocumentRequest = new DocumentRequest();
  @ViewChild('input') myInputVariable: ElementRef;
  @Output() closePopUpModal = new EventEmitter<boolean>();

  documentPrefix: string[] = [];
  documentList: DocumentDetails[] = [];
  currentDocumentRequest = new BehaviorSubject(this.documentRequest);
  public documentGrp: FormGroup;
  public totalfiles: Array<File> = [];
  public totalFileName = [];
  public fileName = [];
  public lengthCheckToaddMore = 0;

  allowedFileSize: string;
  files: any[] = [];
  checked = [];
  showSaveBtn = false;

  displayConfirmModal = false;
  displaySuccessModal = false;
  success = false;
  successContent = '';
  displayFailureNotification = false;
  errorMessage = '';
  showLoadingButtonSave = false;
  uploadedFileName: string[] = [];

  constructor(
    public fileUtil: FileUtil,
    private renderer: Renderer2,
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    public documentService: DocumentService,
    public loadingService: IsLoadingService,
  ) {
    this.translateService.onDefaultLangChange.subscribe(lang => {
      this.successContent = '';
      this.errorMessage = '';
    });
  }

  ngOnInit(): void {
    this.documentPrefix[0] = '';
    this.documentGrp = this.formBuilder.group({
      items: new FormArray([]),
    });
    this.createUploadDocuments()
    this.allowedFileSize = this.fileUtil.formatBytes(environment.edmUploadFileSize, 2);
    this.loadingService.remove();
  }

  createUploadDocuments() {
    const group = this.formBuilder.group({
      doc_type: new FormControl('', Validators.required),
      doc_description: new FormControl('', Validators.required),
      documentFile: File
    });
    this.items.push(group);
  }

  get doc_type() {
    return this.documentGrp.get('doc_type') as FormControl;
  }

  get doc_description() {
    return this.documentGrp.get('doc_description') as FormControl;
  }

  get items(): FormArray {
    return this.documentGrp.get('items') as FormArray;
  }

  addItem(): void {
    this.documentGrp.markAllAsTouched();
    if (this.documentGrp.status === 'VALID') {
      this.createUploadDocuments();
    }
  }

  removeItem(index: number) {
    this.items.removeAt(index);
    this.totalfiles.splice(index, 1);
    this.totalFileName.splice(index, 1);
    this.uploadedFileName.splice(index, 1);
    this.documentList.splice(index, 1);
    this.documentPrefix.splice(index, 1);
    this.fileName.splice(index, 1);
  }

  selectChangeHandler(event, rowIndex) {
    this.documentPrefix[rowIndex] = event.target.value;
    if (this.items.controls[rowIndex].get('doc_description').value !== '') {
      this.totalFileName[rowIndex] = this.documentPrefix[rowIndex] + '_' + this.fileName[rowIndex];
      this.items.controls[rowIndex].get('doc_description').setValue(this.totalFileName[rowIndex]);
      this.items.controls[rowIndex].get('doc_description').disable();
      this.documentList[rowIndex].documentType = this.documentPrefix[rowIndex];
      this.documentList[rowIndex].documentName = this.totalFileName[rowIndex];
    }
  }

  public fileSelectionEvent(fileInput: any, rowIndex) {
    const documentRequest = new DocumentRequest();
    documentRequest.document = new Document();
    const fileReader = new FileReader();
    this.uploadedFileName[rowIndex] = fileInput.target.files[0].name;
    fileReader.readAsDataURL(fileInput.target.files[0]);
    const status = this.fileUtil.checkEdmFileSizeAndType(fileInput.target.files[0]);
    if (status === '') {
      fileReader.onload = (e) => {
        let fileContent = fileReader.result.toString().split(',')[1];
        if (fileContent !== null && fileContent.startsWith('77u/')) {
          fileContent = fileContent.substring(4, fileContent.length);
        }
        documentRequest.document.documentDetails = [];
        const documentDetails = new DocumentDetails();
        documentDetails.documentType = this.documentPrefix[rowIndex];
        documentDetails.documentName = this.documentPrefix[rowIndex] + '_' + fileInput.target.files[0].name;
        this.fileName[rowIndex] = fileInput.target.files[0].name;
        documentDetails.documentName = documentDetails.documentName.trim();
        this.items.controls[rowIndex].get('doc_description').setValue(documentDetails.documentName);
        this.items.controls[rowIndex].get('doc_description').disable();
        documentDetails.documentContent = fileContent;
        documentDetails.active = true;
        documentRequest.document.documentDetails.push(documentDetails);
        if (this.totalfiles.length > rowIndex) {
          this.totalfiles[rowIndex] = fileInput.target.files[0];
          this.totalFileName[rowIndex] = fileInput.target.files[0].name;
          this.documentList[rowIndex] = documentDetails;
        } else {
          this.totalfiles.push(fileInput.target.files[0]);
          this.totalFileName.push(fileInput.target.files[0].name);
          this.documentList.push(documentDetails);
        }
      };
      this.displayFailureNotification = false;
      this.errorMessage = '';
    } else {
      fileInput.srcElement.value = null;
      this.items.controls[rowIndex].get('doc_description').setValue('');
      this.items.controls[rowIndex].get('doc_description').disable();
      this.displayFailureNotification = true;
      this.errorMessage = status;
    }
  }

  fileBrowseHandler(files) {
    this.prepareFilesList(files);
  }

  onFileDropped($event) {
    this.prepareFilesList($event);
  }

  prepareFilesList(files: Array<any>) {

    for (const item of files) {
      this.errorMessage = this.fileUtil.checkEdmFileSizeAndType(item);
      if (this.errorMessage !== '') {
        break;
      }
    }

    if (this.errorMessage !== '') {
      this.displayFailureNotification = true;
      return;
    }

    for (const item of files) {
      item.progress = 0;
      this.files.push(item);
    }

    this.uploadFilesSimulator(0);
    this.readDocument(this.files);
  }

  uploadFilesSimulator(index: number) {

    if (this.checked.find(x => x === true)) {
      this.showSaveBtn = true;
    } else {
      this.showSaveBtn = false;
    }

    setTimeout(() => {
      if (index === this.files.length) {
        return;
      } else {
        const progressInterval = setInterval(() => {
          if (this.files[index].progress === 100) {
            clearInterval(progressInterval);
            this.uploadFilesSimulator(index + 1);
            const data = document.querySelectorAll('input');
            data[index].checked = true;
            this.checked[index] = true;
            this.checkboxFile();
          } else {
            this.files[index].progress += 5;
          }
        }, 100);
      }
    }, 100);
  }

  checkboxFile(index?, event?) {
    if (event && event.target) {
      this.checked[index] = event.target.checked;
      const checkedList = this.checked.filter(x => x === true);
      if (checkedList.length === 0) {
        this.showSaveBtn = false;
      } else {
        this.showSaveBtn = true;
      }
    } else {
      const checkedList = this.checked.filter(x => x === true);
      if (checkedList.length === this.files.length) {
        this.showSaveBtn = true;
      } else {
        this.showSaveBtn = false;
      }
    }
  }

  readDocument(files): Observable<DocumentRequest> {
    this.currentDocumentRequest = new BehaviorSubject(new DocumentRequest());
    const currentDocumentRequest = new DocumentRequest();
    currentDocumentRequest.document = new Document();
    currentDocumentRequest.document.documentDetails = [];
    files.forEach((file, index) => {
      const documentRequest = new DocumentRequest();
      documentRequest.document = new Document();
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = (e) => {
        let fileContent = fileReader.result.toString().split(',')[1];
        if (fileContent !== null && fileContent.startsWith('77u/')) {
          fileContent = fileContent.substring(4, fileContent.length);
        }
        documentRequest.document.documentDetails = [];
        const documentDetails = new DocumentDetails();
        documentDetails.documentType = this.documentRequest.document.entityType;
        documentDetails.documentName = file.name;
        documentDetails.documentContent = fileContent;
        documentDetails.active = true;
        currentDocumentRequest.document.documentDetails.push(documentDetails);
      };
    });
    this.currentDocumentRequest.next(currentDocumentRequest);
    return this.currentDocumentRequest;
  }

  deleteFile() {
    const tempChecked = [];
    const tempUnChecked = [];

    if (this.checked.length > 0) {
      this.checked.forEach((x, index) => {
        if (x === true) {
          tempChecked.push(this.files[index]);
        } else {
          tempUnChecked.push(this.files[index]);
        }
      });
    }

    if (tempUnChecked.length > 0) {
      this.checked = [];
      this.showSaveBtn = false;
      this.files = this.files.filter((x) => tempUnChecked.includes(x));
    } else {
      this.checked = [];
      this.files = [];
    }

    if (this.files.length === 0) {
      this.checked = [];
      this.files = [];
      this.showSaveBtn = false;
    }

    this.readDocument(this.files);
  }

  saveUpload() {
    this.showLoadingButtonSave = true;
    this.documentGrp.markAllAsTouched();
    if (this.documentGrp.status === 'VALID' && this.documentList.length > 0) {
      this.documentRequest.document.businessUnit = this.documentRequest.document.businessUnit;
      this.documentRequest.document.entityNumber = this.documentRequest.document.entityNumber;
      this.documentRequest.document.entityType = this.documentRequest.document.entityType;
      this.documentRequest.document.facility = this.documentRequest.document.facility;
      this.documentRequest.documentExists = this.documentRequest.documentExists;
      this.documentRequest.document.documentDetails = this.documentList;
      this.documentService.uploadNewOrAddadditionalDocuments(this.documentRequest).subscribe(response => {
        if (response.success) {
          this.showLoadingButtonSave = false;
          this.displayFailureNotification = false;
          this.successContent = this.uploadType + this.translateService.instant('COMMON.CommonMessage_Documentuploadedsuccessfully', {Default: 'Document uploaded successfully'});
          this.displaySuccessModal = true;
        }
      }, ((error) => {
        this.displayFailureNotification = true;
        this.displaySuccessModal = false;
        this.showLoadingButtonSave = false;
        this.errorMessage = this.translateService.instant('COMMON.CommonMessage_Systemerroroccuredwhilesavingthefiles', {Default: 'System error occured while saving the files'});
        this.showError(error);
      })
      );
    }
  }

  /** Success and Error handling */
  showError(error: any) {
    let errMessage = '';
    this.errorMessage = this.translateService.instant('COMMON.CommonMessage_Systemerroroccuredwhileuploadingdocuments', {Default: 'System error occured while uploading documents'});
    if (
      (error !== null && error.error.errors.exceptionResponse !== null) ||
      error.error.errors.exceptionResponse !== undefined
    ) {
      for (const errs of error.error.errors.exceptionResponse) {
        if (
          errs.description !== undefined &&
          errs.description !== null &&
          errs.description !== ''
        ) {
        }
        errMessage += ' :: ' + errs.description;
      }
      this.errorMessage += errMessage;
    }
  }

  handleCloseEventForSuccess() {
    this.displaySuccessModal = false;
    this.onClose('close');
  }

  handleCloseErrorEvent() {
    this.displayFailureNotification = false;
  }

  onClose(action) {
    if (action === 'close') {
      this.closePopUpModal.emit(true);
    }
  }

  onSubmit(formVal?) {
    this.onClose('close');
  }

}
